/*
 * Shopify standard storefront events and Shopify.actions for Keystone (24/7).
 *
 * Keystone's own JavaScript (assets/main-*.js) is a vendor build and stays
 * untouched. This module only uses its public surface:
 *   - the Alpine root component on <html x-data="app()">, read with
 *     Alpine.$data(): updateCart(openCart, openAlert) re-fetches /cart.js and
 *     re-renders the cart drawer, the cart page and the count; openCart()
 *     opens the cart drawer;
 *   - the AJAX cart requests the theme sends (/cart/add.js, /cart/change.js,
 *     /cart/update.js), observed through a small window.fetch wrapper.
 *
 * Loaded from layout/theme.liquid only when the theme setting
 * "enable_standard_storefront_events" is on. Shopify.actions are configured
 * from an inline DOMContentLoaded listener placed before content_for_header,
 * which calls configureShopifyActions() below.
 */
import {
  CartErrorEvent,
  CartLinesUpdateEvent,
  SearchUpdateEvent,
  createViewEventElement,
} from 'https://cdn.shopify.com/storefront/standard-events.js';

const ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
const CART_MUTATION = /\/cart\/(add|change|update)\.js$/;
const INTERACTION_TTL = 10000;

/* ---------- Product view: <s-view-event> ---------- */

if (!customElements.get('s-view-event')) {
  customElements.define('s-view-event', createViewEventElement());
}

/* ---------- Alpine root (Keystone's public surface) ---------- */

function alpineRoot() {
  const alpine = window.Alpine;
  if (!alpine || typeof alpine.$data !== 'function') return null;
  const data = alpine.$data(document.documentElement);
  return data && typeof data.updateCart === 'function' ? data : null;
}

function cartItems() {
  const data = alpineRoot();
  return (data && data.cart && Array.isArray(data.cart.items) && data.cart.items) || [];
}

/* ---------- Context of the theme's own cart changes ---------- */

let lastInteraction = null;
let lastInteractionAt = 0;
const remember = (event) => {
  if (event.target instanceof Element) {
    lastInteraction = event.target;
    lastInteractionAt = Date.now();
  }
};
['pointerdown', 'keydown', 'submit', 'change'].forEach((type) =>
  document.addEventListener(type, remember, { capture: true, passive: true })
);

function interactionElement() {
  const fresh = lastInteraction && lastInteraction.isConnected && Date.now() - lastInteractionAt < INTERACTION_TTL;
  return fresh ? lastInteraction : null;
}

function contextFor(element) {
  const onCartPage = document.body.classList.contains('cart');
  if (!element) return onCartPage ? 'cart' : 'product';
  if (element.closest('#js-quickAdd')) return 'dialog';
  if (element.closest('#shopify-section-theme__cart')) return 'cart';
  if (onCartPage && element.closest('main')) return 'cart';
  return 'product';
}

/* ---------- Reading AJAX cart request bodies ---------- */

function formToObject(form) {
  const object = {};
  const items = [];
  for (const [key, value] of form.entries()) {
    const match = key.match(/^items\[(\d+)\]\[(id|quantity)\]$/);
    if (match) {
      (items[match[1]] ||= {})[match[2]] = value;
    } else if (key.startsWith('updates[')) {
      (object.updates ||= {})[key.slice(8, -1)] = value;
    } else if (!(key in object)) {
      object[key] = value;
    }
  }
  if (items.length) object.items = items.filter(Boolean);
  return object;
}

function readBody(body) {
  if (body == null) return null;
  if (typeof body === 'string') {
    try {
      return JSON.parse(body);
    } catch {
      return formToObject(new URLSearchParams(body));
    }
  }
  if (body instanceof FormData || body instanceof URLSearchParams) return formToObject(body);
  return null;
}

function lineKeyFor(id) {
  const items = cartItems();
  const value = String(id);
  if (value.includes(':')) return value;
  const item = items.find((entry) => String(entry.variant_id) === value || String(entry.id) === value);
  return item && item.key ? item.key : value;
}

const isKnownVariant = (id) => {
  const value = String(id);
  return value.includes(':') || cartItems().some((entry) => String(entry.variant_id) === value || entry.key === value);
};

function toQuantity(value, fallback) {
  const number = Number(value);
  return Number.isFinite(number) && value !== '' && value != null ? number : fallback;
}

/** Turns an AJAX cart request into the lines-update events it stands for. */
function describeRequest(path, body) {
  if (!body) return [];

  if (path === 'add') {
    const items = Array.isArray(body.items) ? body.items : [{ id: body.id, quantity: body.quantity }];
    const lines = items
      .filter((item) => item && item.id != null && item.id !== '')
      .map((item) => ({ merchandiseId: String(item.id), quantity: toQuantity(item.quantity, 1) }))
      .filter((line) => line.quantity > 0);
    return lines.length ? [{ action: 'add', lines }] : [];
  }

  if (path === 'change') {
    const quantity = toQuantity(body.quantity, null);
    if (quantity === null) return [];
    let id = null;
    if (body.line != null) {
      const item = cartItems()[Number(body.line) - 1];
      id = item && item.key;
    } else if (body.id != null) {
      id = lineKeyFor(body.id);
    }
    if (!id) return [];
    return [{ action: quantity === 0 ? 'remove' : 'update', lines: [{ id, quantity }] }];
  }

  // update.js: only line changes ("updates") are lines-update events. Note and
  // attribute-only requests (Keystone's cart note) are not in scope.
  const updates = body.updates;
  if (!updates || typeof updates !== 'object') return [];
  const entries = Array.isArray(updates)
    ? updates.map((quantity, index) => [cartItems()[index] && cartItems()[index].key, quantity])
    : Object.entries(updates);
  const groups = { add: [], update: [], remove: [] };
  for (const [id, rawQuantity] of entries) {
    const quantity = toQuantity(rawQuantity, null);
    if (!id || quantity === null) continue;
    if (!isKnownVariant(id)) {
      if (quantity > 0) groups.add.push({ merchandiseId: String(id), quantity });
      continue;
    }
    groups[quantity === 0 ? 'remove' : 'update'].push({ id: lineKeyFor(id), quantity });
  }
  return Object.entries(groups)
    .filter(([, lines]) => lines.length)
    .map(([action, lines]) => ({ action, lines }));
}

/* ---------- Lines-update events around the theme's AJAX cart requests ---------- */

function startCartLinesUpdate({ action, lines, context, target }) {
  const deferred = CartLinesUpdateEvent.createPromise();
  const event = new CartLinesUpdateEvent({ action, context, lines, promise: deferred.promise });
  // A rejection nobody listens to must not show up as "Uncaught (in promise)".
  event.promise.catch(() => {});
  (target || document).dispatchEvent(event);
  return deferred;
}

function dispatchCartError(target, message, code) {
  (target && target.isConnected ? target : document).dispatchEvent(
    new CartErrorEvent({ error: message, code })
  );
}

const originalFetch = window.fetch;

function requestPath(input) {
  try {
    const url = new URL(input instanceof Request ? input.url : String(input), location.href);
    if (url.origin !== location.origin) return null;
    const match = url.pathname.match(CART_MUTATION);
    return match ? match[1] : null;
  } catch {
    return null;
  }
}

async function fetchCartJson() {
  const response = await originalFetch.call(window, `${ROOT}cart.js`, { headers: { Accept: 'application/json' } });
  if (!response.ok) throw new Error(`cart.js ${response.status}`);
  return response.json();
}

async function observedFetch(input, init) {
  const path = input instanceof Request ? null : requestPath(input);
  const method = String((init && init.method) || 'GET').toUpperCase();
  const changes = path && method === 'POST' ? describeRequest(path, readBody(init && init.body)) : [];
  if (!changes.length) return originalFetch.call(window, input, init);

  const target = interactionElement();
  const context = contextFor(target);
  const pending = changes.map((change) => startCartLinesUpdate({ ...change, context, target }));
  const settle = (fn) => pending.forEach(fn);

  let response;
  try {
    response = await originalFetch.call(window, input, init);
  } catch (error) {
    dispatchCartError(target, error && error.message ? error.message : 'Network error', 'SERVICE_UNAVAILABLE');
    settle((deferred) => deferred.reject(error));
    throw error;
  }

  try {
    if (!response.ok) {
      const data = await response.clone().json().catch(() => null);
      const message = (data && (data.description || data.message)) || `HTTP ${response.status}`;
      dispatchCartError(target, message, response.status >= 400 && response.status < 500 ? 'INVALID' : 'SERVICE_UNAVAILABLE');
      settle((deferred) => deferred.reject(new Error(message)));
    } else {
      // change.js and update.js answer with the cart; add.js only with the added items.
      const json = path === 'add' ? await fetchCartJson() : await response.clone().json();
      const result = { cart: CartLinesUpdateEvent.createCartFromAjaxResponse(json) };
      settle((deferred) => deferred.resolve(result));
    }
  } catch (error) {
    settle((deferred) => deferred.reject(error));
  }
  return response;
}

if (typeof originalFetch === 'function' && !originalFetch.__standardStorefrontEvents) {
  observedFetch.__standardStorefrontEvents = true;
  window.fetch = observedFetch;
}

/* ---------- Search results page ---------- */

function startSearchUpdate(target, totalCount) {
  const params = new URLSearchParams(location.search);
  const deferred = SearchUpdateEvent.createPromise();
  const search = { query: params.get('q') || '' };
  const productFilters = SearchUpdateEvent.parseProductFilters(params);
  const sortKey = SearchUpdateEvent.getSortKey(params);
  if (productFilters) search.productFilters = productFilters;
  if (sortKey) search.sortKey = sortKey;
  const event = new SearchUpdateEvent({ search, promise: deferred.promise });
  event.promise.catch(() => {});
  (target || document).dispatchEvent(event);
  deferred.resolve({ totalCount });
}

// Rendered in sections/search__main.liquid. Keystone re-renders that section
// on every filter or sort change, so each render inserts a new element.
if (!customElements.get('theme-search-update-event')) {
  customElements.define(
    'theme-search-update-event',
    class extends HTMLElement {
      connectedCallback() {
        setTimeout(() => {
          if (this.isConnected) startSearchUpdate(this, Number(this.dataset.totalCount) || 0);
        });
      }
    }
  );
}

/* ---------- Shopify.actions ---------- */

async function refreshCart(result) {
  const data = alpineRoot();
  if (!data) throw new Error('Keystone cart not found');
  // Re-fetches /cart.js and re-renders drawer, cart page and count. No drawer, no alert.
  await data.updateCart(false, false);
  // Keystone's updateCart swallows its own errors; compare counts to notice them.
  const expected = result && result.cart ? result.cart.totalQuantity : undefined;
  if (typeof expected === 'number' && data.cart.item_count !== expected) {
    throw new Error('Keystone cart did not refresh');
  }
}

function configureShopifyActions(options = {}) {
  const actions = window.Shopify && window.Shopify.actions;
  if (!actions) return;

  if (actions.updateCart && typeof actions.updateCart.configure === 'function') {
    actions.updateCart.configure({
      handler: async (defaultHandler) => {
        const result = await defaultHandler();
        await refreshCart(result).catch(() => window.location.reload());
        return result;
      },
    });
  }

  if (actions.openCart && typeof actions.openCart.configure === 'function') {
    actions.openCart.configure({
      handler: async (defaultHandler) => {
        const data = alpineRoot();
        if (options.cartIconBehavior !== 'drawer' || !data || typeof data.openCart !== 'function') {
          window.location.href = (window.Shopify.routes && window.Shopify.routes.cart_url) || `${ROOT}cart`;
          return;
        }
        data.openCart();
      },
    });
  }
}

window.KeystoneStandardEvents = { startCartLinesUpdate, startSearchUpdate, configureShopifyActions };
